import React, { useEffect, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/reviews`)
      .then((res) => {
        if (mounted) setReviews(res.data || []);
      })
      .catch((e) => console.error("reviews fetch failed", e));
    return () => {
      mounted = false;
    };
  }, []);

  const next = () =>
    setActive((a) => (reviews.length ? (a + 1) % reviews.length : 0));
  const prev = () =>
    setActive((a) =>
      reviews.length ? (a - 1 + reviews.length) % reviews.length : 0,
    );

  const current = reviews[active];

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative pt-7 md:pt-12 pb-28 md:pb-40 bg-[#050505] overflow-hidden"
    >
      {/* Decorative giant quote mark */}
      <span
        aria-hidden
        className="absolute -top-10 -left-6 md:-left-2 font-display text-[#C5A059]/8 text-[24rem] leading-none select-none"
        style={{ color: "rgba(197,160,89,0.06)" }}
      >
        &ldquo;
      </span>

      <div className="relative max-w-6xl mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex items-center gap-4 mb-14">
          <span className="overline">03 — Reviews</span>
          <span className="gold-rule" />
        </div>

        <Reveal>
          <h2
            data-testid="reviews-headline"
            className="font-display text-[#F5F5F0] text-4xl sm:text-5xl md:text-6xl leading-[1.05] max-w-3xl"
          >
            What they say
            <span className="font-italic-display text-[#C5A059]"> afterward</span>.
          </h2>
        </Reveal>

        {/* Carousel */}
        <div className="mt-16 min-h-[280px]" data-testid="reviews-carousel">
          {current ? (
            <div key={current.id}>
              <div className="flex gap-1 mb-6" aria-label={`Rating: ${current.rating} stars`}>
                {Array.from({ length: current.rating || 5 }).map((_, i) => (
                  <Star key={i} className="star" fill="currentColor" stroke="none" />
                ))}
              </div>
              <blockquote
                data-testid="review-quote"
                className="font-italic-display text-[#F5F5F0] text-2xl md:text-4xl lg:text-5xl leading-[1.15] max-w-4xl"
              >
                &ldquo;{current.quote}&rdquo;
              </blockquote>
              <footer className="mt-10 flex items-center gap-5">
                <span className="w-12 h-12 rounded-full border border-[#C5A059]/40 flex items-center justify-center font-display text-[#C5A059] text-lg">
                  {current.author
                    .split(" ")
                    .map((p) => p[0])
                    .slice(0, 2)
                    .join("")}
                </span>
                <div>
                  <div
                    data-testid="review-author"
                    className="font-display text-[#F5F5F0] text-xl"
                  >
                    {current.author}
                  </div>
                  <div className="overline text-[10px]">{current.role}</div>
                </div>
              </footer>
            </div>
          ) : (
            <div className="text-[#A1A1AA] font-italic-display text-2xl">
              Loading testimonials…
            </div>
          )}
        </div>

        {/* Controls */}
        {reviews.length > 0 && (
          <div className="mt-14 flex items-center justify-between border-t border-[#C5A059]/15 pt-8">
            <div className="flex items-center gap-3">
              <button
                data-testid="reviews-prev-btn"
                onClick={prev}
                aria-label="Previous review"
                className="w-12 h-12 rounded-full border border-[#C5A059]/30 hover:border-[#C5A059] hover:bg-[#C5A059] hover:text-black text-[#C5A059] flex items-center justify-center transition-all"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                data-testid="reviews-next-btn"
                onClick={next}
                aria-label="Next review"
                className="w-12 h-12 rounded-full border border-[#C5A059]/30 hover:border-[#C5A059] hover:bg-[#C5A059] hover:text-black text-[#C5A059] flex items-center justify-center transition-all"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <div className="overline text-[#A1A1AA]">
              <span className="text-[#C5A059]">
                {String(active + 1).padStart(2, "0")}
              </span>
              {" / "}
              {String(reviews.length).padStart(2, "0")}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
